import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { ADMIN } from '@/constants/testIds';
import AnalyticsTab from '@/components/AnalyticsTab';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'shs_admin_token';

const STATUSES = ['new', 'reviewing', 'quoted', 'in_progress', 'shipped', 'done', 'declined'];
const STATUS_STYLES = {
  new:         'bg-brand-orange text-black',
  reviewing:   'bg-yellow-300 text-black',
  quoted:      'bg-blue-300 text-black',
  in_progress: 'bg-purple-300 text-black',
  shipped:     'bg-green-300 text-black',
  done:        'bg-green-500 text-black',
  declined:    'bg-red-400 text-black',
};

const PHOTO_CATEGORIES = [
  'Studio', 'Custom', 'Faith', 'Brand', 'Bulk', 'Business',
  'Drop', 'In the wild', 'Memorial', 'Other',
];

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const Money = ({ amount, currency = 'usd' }) => (
  <span>${Number(amount || 0).toFixed(2)} <span className="text-white/40 text-xs uppercase">{currency}</span></span>
);

// ── Login ─────────────────────────────────────────────────────────────────
const Login = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err?.response?.status === 401 ? 'Wrong password.' : 'Could not log in.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
      <form
        data-testid={ADMIN.loginForm}
        onSubmit={submit}
        className="w-full max-w-md p-8 sm:p-10 rounded-3xl bg-[#141414] border-2 border-white/10 shadow-[8px_8px_0_#FF6600]"
      >
        <p className="font-body text-xs tracking-[0.4em] uppercase text-brand-orange mb-3">
          Shirt Happens Studio →
        </p>
        <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight mb-2">
          Admin.
        </h1>
        <p className="font-body text-sm text-white/60 mb-7">
          Drop the password to manage requests, orders, and exports.
        </p>
        <label htmlFor="admin-password" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">
          Password
        </label>
        <input
          id="admin-password"
          data-testid={ADMIN.loginPassword}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-black border-2 border-white/15 rounded-xl px-4 py-3 text-white font-body focus:outline-none focus:border-brand-orange"
          placeholder="••••••••"
          required
        />
        {error && (
          <p data-testid={ADMIN.loginError} className="mt-4 text-red-300 font-body text-sm">{error}</p>
        )}
        <button
          type="submit"
          data-testid={ADMIN.loginSubmit}
          disabled={loading}
          className="mt-6 w-full bg-brand-orange text-black font-display font-black uppercase tracking-[0.2em] py-3.5 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Enter studio →'}
        </button>
        <p className="mt-6 text-center text-xs text-white/30">
          <a href="/" className="hover:text-white/70 transition-colors">← back to site</a>
        </p>
      </form>
    </div>
  );
};

// ── Stat tile ─────────────────────────────────────────────────────────────
const Stat = ({ label, value, testid, accent }) => (
  <div
    data-testid={testid}
    className="p-5 rounded-2xl bg-[#141414] border-2 border-white/10 hover:border-brand-orange/40 transition-colors"
  >
    <div className="text-[10px] tracking-[0.32em] uppercase text-white/50 mb-2">{label}</div>
    <div className={`font-display font-black text-3xl sm:text-4xl ${accent ? 'text-brand-orange' : 'text-white'}`}>
      {value}
    </div>
  </div>
);

// ── Send-payment-link dialog ─────────────────────────────────────────────
const SendPaymentLinkDialog = ({ token, request, onClose, onSent }) => {
  const [amount, setAmount] = useState('');
  const [productName, setProductName] = useState(
    `Custom · ${request.garment || 'Shirt'}${request.shirt_color ? ` · ${request.shirt_color}` : ''}`,
  );
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError('Enter a positive dollar amount.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/requests/${request.id}/send-payment-link`,
        {
          amount: value,
          product_name: productName.trim() || null,
          message: message.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSent(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send the payment link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
    >
      <form
        data-testid={ADMIN.payLinkDialog}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-[#141414] rounded-3xl border-2 border-brand-orange/60 shadow-[8px_8px_0_#FF6600] overflow-hidden"
      >
        <div className="px-6 sm:px-8 py-5 border-b border-white/10 bg-brand-orange/10">
          <div className="text-[10px] tracking-[0.32em] uppercase text-brand-orange">Send Stripe payment link →</div>
          <div className="font-display font-black text-2xl text-white mt-1">Quote & invoice</div>
          <div className="text-xs text-white/60 mt-1">
            {request.name} · <span className="text-brand-orange">{request.email}</span>
          </div>
        </div>

        <div className="px-6 sm:px-8 py-6 space-y-5 text-sm text-white/80">
          <div>
            <label htmlFor="paylink-amount" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">
              Amount to charge (USD)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-display font-black">$</span>
              <input
                id="paylink-amount"
                data-testid={ADMIN.payLinkAmount}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
                placeholder="65.00"
                className="w-full bg-black border-2 border-white/15 rounded-xl pl-9 pr-4 py-3 text-white font-display font-black text-2xl focus:outline-none focus:border-brand-orange"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="paylink-product" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">
              Product / description
            </label>
            <input
              id="paylink-product"
              data-testid={ADMIN.payLinkProduct}
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              maxLength={200}
              className="w-full bg-black border-2 border-white/15 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-brand-orange"
            />
          </div>

          <div>
            <label htmlFor="paylink-message" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">
              Personal message (optional)
            </label>
            <textarea
              id="paylink-message"
              data-testid={ADMIN.payLinkMessage}
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1500}
              placeholder="Attach a sketch note, timeline confirmation, or a personal thank-you."
              className="w-full bg-black border-2 border-white/15 rounded-xl px-4 py-3 text-white text-sm leading-relaxed resize-none focus:outline-none focus:border-brand-orange"
            />
          </div>

          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 sm:px-8 py-5 border-t border-white/10 bg-black/40 flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid={ADMIN.payLinkCancel}
            onClick={onClose}
            className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs uppercase tracking-[0.2em] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid={ADMIN.payLinkSubmit}
            disabled={submitting || !amount}
            className="px-6 py-2.5 rounded-full bg-brand-orange hover:bg-white text-black font-display font-black text-xs uppercase tracking-[0.2em] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Sending…' : 'Send payment link →'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Convert-to-order dialog ──────────────────────────────────────────────
const ConvertToOrderDialog = ({ token, request, onClose, onConverted }) => {
  const [amount, setAmount] = useState('');
  const [productName, setProductName] = useState(
    `Custom · ${request.garment || 'Shirt'}${request.shirt_color ? ` · ${request.shirt_color}` : ''}`,
  );
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError('Enter a positive dollar amount.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/requests/${request.id}/convert-to-order`,
        { amount: value, product_name: productName.trim() || null, notes: notes.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onConverted(data.order);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not convert this request. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
    >
      <form
        data-testid={ADMIN.convertDialog}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-[#141414] rounded-3xl border-2 border-green-500/40 shadow-[8px_8px_0_#22c55e] overflow-hidden"
      >
        <div className="px-6 sm:px-8 py-5 border-b border-white/10 bg-green-500/10">
          <div className="text-[10px] tracking-[0.32em] uppercase text-green-300">Convert to paid order →</div>
          <div className="font-display font-black text-2xl text-white mt-1">Log the sale</div>
          <div className="text-xs text-white/60 mt-1">
            {request.name} · <span className="text-brand-orange">{request.email}</span>
          </div>
        </div>

        <div className="px-6 sm:px-8 py-6 space-y-5 text-sm text-white/80">
          <div>
            <label htmlFor="convert-amount" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">
              Amount received (USD)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-display font-black">$</span>
              <input
                id="convert-amount"
                data-testid={ADMIN.convertAmount}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
                placeholder="45.00"
                className="w-full bg-black border-2 border-white/15 rounded-xl pl-9 pr-4 py-3 text-white font-display font-black text-2xl focus:outline-none focus:border-green-400"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="convert-product" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">
              Product / description
            </label>
            <input
              id="convert-product"
              data-testid={ADMIN.convertProduct}
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              maxLength={200}
              className="w-full bg-black border-2 border-white/15 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-green-400"
            />
            <div className="text-[10px] text-white/40 mt-1">Shown on the Orders tab and in Analytics.</div>
          </div>

          <div>
            <label htmlFor="convert-notes" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">
              Notes (optional)
            </label>
            <textarea
              id="convert-notes"
              data-testid={ADMIN.convertNotes}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              placeholder="How they paid, delivery date, anything else."
              className="w-full bg-black border-2 border-white/15 rounded-xl px-4 py-3 text-white text-sm leading-relaxed resize-none focus:outline-none focus:border-green-400"
            />
          </div>

          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 sm:px-8 py-5 border-t border-white/10 bg-black/40 flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid={ADMIN.convertCancel}
            onClick={onClose}
            className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs uppercase tracking-[0.2em] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid={ADMIN.convertSubmit}
            disabled={submitting || !amount}
            className="px-6 py-2.5 rounded-full bg-green-500 hover:bg-green-600 text-black font-display font-black text-xs uppercase tracking-[0.2em] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Recording…' : 'Log paid order →'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Email-status pill ────────────────────────────────────────────────────
const EmailPill = ({ label, status, error }) => {
  const styles = {
    sent:    'bg-green-500/20 text-green-200 border-green-500/30',
    failed:  'bg-red-500/20 text-red-200 border-red-500/40',
    skipped: 'bg-white/10 text-white/50 border-white/15',
  };
  const dot = { sent: '●', failed: '✕', skipped: '○' };
  const tone = styles[status] || styles.skipped;
  const icon = dot[status] || '–';
  return (
    <span
      title={error || (status ? `${label}: ${status}` : `${label}: not sent`)}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-display font-black tracking-[0.16em] uppercase border ${tone}`}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </span>
  );
};

// ── Request row ───────────────────────────────────────────────────────────
const RequestRow = ({ item, onStatusChange, onConvert, onSendPayLink, onDelete, onUnconvert }) => {
  const [open, setOpen] = useState(false);
  const styles = STATUS_STYLES[item.status] || 'bg-white/20 text-white';
  const isConverted = Boolean(item.converted_order_session_id);
  const hasPayLink = Boolean(item.payment_link_url) && !isConverted;
  return (
    <div data-testid={`${ADMIN.requestRow}-${item.id}`} className="rounded-2xl bg-[#141414] border-2 border-white/10">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left p-5 grid grid-cols-12 gap-4 items-center hover:bg-white/[0.02] transition-colors rounded-2xl"
      >
        <div className="col-span-12 sm:col-span-3">
          <div className="font-display font-black text-white text-base truncate">{item.name}</div>
          <a
            href={`mailto:${item.email}`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-brand-orange hover:underline break-all"
          >
            {item.email}
          </a>
        </div>
        <div className="col-span-6 sm:col-span-4 text-white/70 text-sm truncate">{item.idea}</div>
        <div className="col-span-3 sm:col-span-2 text-xs text-white/50">
          {item.garment || '—'} · {item.shirt_color || '—'} · {item.size || '—'}
        </div>
        <div className="col-span-3 sm:col-span-2 text-xs text-white/50">
          <div>{fmtDateTime(item.submitted_at)}</div>
          <div className="flex flex-wrap gap-1 mt-1">
            <EmailPill label="Owner" status={item.email_owner_status} error={item.email_owner_error} />
            <EmailPill label="Reply" status={item.email_autoreply_status} error={item.email_autoreply_error} />
          </div>
        </div>
        <div className="col-span-12 sm:col-span-1 flex justify-end">
          <select
            data-testid={`${ADMIN.statusSelect}-${item.id}`}
            value={item.status || 'new'}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onStatusChange(item.id, e.target.value)}
            className={`appearance-none px-3 py-1.5 rounded-full text-[10px] tracking-[0.18em] uppercase font-display font-black border-0 cursor-pointer ${styles}`}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s} className="bg-black text-white">{s.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
          <div className="md:col-span-2 p-4 rounded-xl bg-black/50 border border-white/5 whitespace-pre-wrap text-white/85 leading-relaxed">
            {item.idea}
            {Array.isArray(item.items) && item.items.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="text-[10px] tracking-[0.32em] uppercase text-brand-orange mb-2">
                  Order breakdown · {item.items.reduce((s, x) => s + (Number(x.quantity) || 0), 0)} pieces
                </div>
                <div className="rounded-xl overflow-hidden border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5 text-white/60 uppercase tracking-[0.18em] text-[10px]">
                      <tr>
                        <th className="text-left px-3 py-2">Garment</th>
                        <th className="text-left px-3 py-2">Color</th>
                        <th className="text-left px-3 py-2">Size</th>
                        <th className="text-right px-3 py-2">Qty</th>
                      </tr>
                    </thead>
                    <tbody className="text-white/85 divide-y divide-white/5">
                      {item.items.map((li, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2">{li.garment || '—'}</td>
                          <td className="px-3 py-2">{li.color || '—'}</td>
                          <td className="px-3 py-2">{li.size || '—'}</td>
                          <td className="px-3 py-2 text-right font-display font-black text-brand-orange">{li.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {(() => {
              const refs = (Array.isArray(item.reference_images) && item.reference_images.length > 0)
                ? item.reference_images
                : (item.reference_image ? [{ url: item.reference_image, filename: item.reference_filename }] : []);
              if (refs.length === 0) return null;
              return (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-brand-orange mb-2">
                    Customer attached · {refs.length} file{refs.length === 1 ? '' : 's'}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {refs.map((r, i) => {
                      const url = r.url || '';
                      const fname = r.filename || 'reference';
                      const isImg = /\.(jpe?g|png|webp|gif|heic|heif|bmp|tiff?)$/i.test(url);
                      return (
                        <a
                          key={i}
                          href={`${API}${url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group block rounded-lg overflow-hidden bg-black border border-white/10 hover:border-brand-orange transition-colors"
                          download={fname}
                          data-testid={`admin-ref-file-${item.id}-${i}`}
                        >
                          <div className="aspect-square flex items-center justify-center">
                            {isImg ? (
                              <img src={`${API}${url}`} alt={fname} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs font-display font-black text-brand-orange">
                                {(url.split('.').pop() || 'file').toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="px-2 py-1 text-[10px] text-white/70 group-hover:text-brand-orange truncate">
                            {fname}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-[10px] text-white/40">
                    Click any thumbnail to open or save it.
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="space-y-2 text-white/70">
            {!(Array.isArray(item.items) && item.items.length > 0) && (
              <>
                <Field k="Garment" v={item.garment} />
                <Field k="Color" v={item.shirt_color} />
                <Field k="Size" v={item.size} />
                <Field k="Quantity" v={item.quantity} />
              </>
            )}
            <Field k="Timeline" v={item.timeline} />
            <Field k="Occasion" v={item.occasion} />
            <Field k="Submitted" v={fmtDateTime(item.submitted_at)} />
            <div className="pt-2 mt-2 border-t border-white/10">
              <div className="text-[10px] tracking-[0.32em] uppercase text-white/40 mb-1">Email delivery</div>
              <div className="flex flex-wrap gap-1.5">
                <EmailPill label="Owner notif" status={item.email_owner_status} error={item.email_owner_error} />
                <EmailPill label="Customer reply" status={item.email_autoreply_status} error={item.email_autoreply_error} />
              </div>
              {(item.email_owner_error || item.email_autoreply_error) && (
                <div className="mt-2 text-[11px] text-red-300/80 leading-relaxed break-all">
                  {item.email_owner_error && <div>Owner: {item.email_owner_error}</div>}
                  {item.email_autoreply_error && <div>Reply: {item.email_autoreply_error}</div>}
                </div>
              )}
            </div>
            <a
              href={`mailto:${item.email}?subject=Re:%20your%20Shirt%20Happens%20idea`}
              className="inline-block mt-2 px-4 py-2 rounded-full bg-brand-orange text-black font-display font-black text-[11px] tracking-[0.2em] uppercase hover:opacity-90"
            >
              Reply →
            </a>
            {isConverted ? (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <div
                  data-testid={ADMIN.convertedBadge}
                  className="inline-block px-4 py-2 rounded-full bg-green-500/20 text-green-300 border border-green-500/30 font-display font-black text-[11px] tracking-[0.2em] uppercase"
                >
                  ✓ Converted to paid order
                </div>
                <button
                  type="button"
                  data-testid={`${ADMIN.unconvert}-${item.id}`}
                  onClick={() => {
                    if (window.confirm('Undo the "converted to paid" status? This lets you re-enter the order amount. Use this if the linked paid order was deleted.')) {
                      onUnconvert(item);
                    }
                  }}
                  title="Clear the converted flag so you can re-mark this as paid"
                  className="inline-block px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white font-display font-black text-[10px] tracking-[0.2em] uppercase transition-colors"
                >
                  ↻ Undo
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  data-testid={`${ADMIN.payLinkOpen}-${item.id}`}
                  onClick={() => onSendPayLink(item)}
                  className="mt-2 mr-2 inline-block px-4 py-2 rounded-full bg-brand-orange hover:bg-white text-black font-display font-black text-[11px] tracking-[0.2em] uppercase transition-colors"
                >
                  {hasPayLink ? '↻ Resend pay link' : 'Send pay link →'}
                </button>
                <button
                  type="button"
                  data-testid={`${ADMIN.convertOpen}-${item.id}`}
                  onClick={() => onConvert(item)}
                  className="mt-2 inline-block px-4 py-2 rounded-full bg-green-500 hover:bg-green-600 text-black font-display font-black text-[11px] tracking-[0.2em] uppercase transition-colors"
                >
                  Mark as paid $
                </button>
                {hasPayLink && (
                  <div
                    data-testid={ADMIN.payLinkSentBadge}
                    className="mt-3 text-xs text-brand-orange break-all leading-relaxed"
                  >
                    ✉ Payment link sent · ${(item.payment_link_amount || 0).toFixed(2)} ·{' '}
                    <a href={item.payment_link_url} target="_blank" rel="noopener noreferrer" className="underline">
                      open Stripe link
                    </a>
                  </div>
                )}
              </>
            )}
            <div className="pt-4 mt-4 border-t border-white/5">
              <button
                type="button"
                data-testid={`${ADMIN.deleteRequest}-${item.id}`}
                onClick={() => onDelete(item)}
                className="text-[11px] tracking-[0.2em] uppercase text-red-400/70 hover:text-red-300 font-display font-black transition-colors"
              >
                🗑 Delete this request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ k, v }) => (
  <div className="flex justify-between gap-3 text-xs">
    <span className="uppercase tracking-[0.2em] text-white/40">{k}</span>
    <span className="text-white/80 text-right">{v || '—'}</span>
  </div>
);

// ── Order row ─────────────────────────────────────────────────────────────
const OrderRow = ({ item, onDelete }) => {
  const paid = item.payment_status === 'paid';
  return (
    <div
      data-testid={`${ADMIN.orderRow}-${item.session_id}`}
      className="rounded-2xl bg-[#141414] border-2 border-white/10 p-5 grid grid-cols-12 gap-4 items-center"
    >
      <div className="col-span-12 sm:col-span-4">
        <div className="font-display font-black text-white text-base truncate">{item.product_name}</div>
        <div className="text-xs text-white/40 truncate">{item.session_id}</div>
      </div>
      <div className="col-span-4 sm:col-span-2 text-sm text-white/70">{item.size || '—'}</div>
      <div className="col-span-4 sm:col-span-2 font-display font-black text-white"><Money amount={item.amount} currency={item.currency} /></div>
      <div className="col-span-4 sm:col-span-2 text-xs text-white/50">{fmtDateTime(item.paid_at || item.created_at)}</div>
      <div className="col-span-12 sm:col-span-2 flex sm:justify-end items-center gap-2">
        <span className={`px-3 py-1.5 rounded-full text-[10px] tracking-[0.18em] uppercase font-display font-black ${paid ? 'bg-green-400 text-black' : 'bg-white/15 text-white/70'}`}>
          {item.payment_status || 'pending'}
        </span>
        <button
          type="button"
          data-testid={`${ADMIN.deleteOrder}-${item.session_id}`}
          onClick={() => onDelete(item)}
          title="Delete this order record"
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-red-500/20 text-white/40 hover:text-red-300 transition-colors text-sm"
        >
          🗑
        </button>
      </div>
    </div>
  );
};

// ── Photo upload modal ───────────────────────────────────────────────────
const PhotoUploadModal = ({ token, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Studio');
  const [preview, setPreview] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    setIsVideo((file.type || '').startsWith('video/'));
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Pick a file first.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim() || file.name);
      fd.append('description', description.trim());
      fd.append('category', category);
      await axios.post(`${API}/api/admin/gallery`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        // browser sets multipart boundary automatically
      });
      onUploaded();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Upload failed. Try a JPG, PNG, WebP, MP4 or MOV.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
    >
      <form
        data-testid={ADMIN.photoUploadForm}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-3xl bg-[#141414] rounded-3xl border-2 border-white/10 shadow-[8px_8px_0_#FF6600] overflow-hidden"
      >
        <div className="px-6 sm:px-8 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-brand-orange">New Photo Dump entry →</div>
            <div className="font-display font-black text-2xl text-white mt-1">Add a photo or video</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-10 h-10 rounded-full bg-white text-black font-display font-black text-lg flex items-center justify-center hover:bg-brand-orange transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Preview / picker */}
          <div className="space-y-3">
            <label
              htmlFor="photo-file"
              className="block aspect-[4/5] rounded-2xl border-2 border-dashed border-white/20 bg-black/40 overflow-hidden cursor-pointer hover:border-brand-orange transition-colors relative"
            >
              {!preview && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <div className="text-4xl text-brand-orange mb-3">+</div>
                  <div className="font-display font-black text-white text-base mb-1">Click to pick a file</div>
                  <div className="text-xs text-white/50">JPG · PNG · WebP · HEIC · MP4 · MOV</div>
                </div>
              )}
              {preview && !isVideo && (
                <img src={preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
              )}
              {preview && isVideo && (
                <video src={preview} muted playsInline controls className="absolute inset-0 w-full h-full object-cover bg-black" />
              )}
            </label>
            <input
              id="photo-file"
              data-testid={ADMIN.photoUploadFile}
              type="file"
              accept="image/*,video/*,.heic,.heif"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />
            {file && (
              <div className="text-xs text-white/50 break-all">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>
            )}
          </div>

          {/* Fields */}
          <div className="space-y-4">
            <div>
              <label htmlFor="photo-title" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">Title</label>
              <input
                id="photo-title"
                data-testid={ADMIN.photoUploadTitle}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Memorial Tee — Cpl. Luis Arreola"
                maxLength={120}
                className="w-full bg-black border-2 border-white/15 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-brand-orange"
                required
              />
            </div>
            <div>
              <label htmlFor="photo-cat" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">Category</label>
              <select
                id="photo-cat"
                data-testid={ADMIN.photoUploadCategory}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-black border-2 border-white/15 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-brand-orange"
              >
                {PHOTO_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="photo-desc" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">Description</label>
              <textarea
                id="photo-desc"
                data-testid={ADMIN.photoUploadDescription}
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="One or two lines about the piece — who, what, why."
                maxLength={1000}
                className="w-full bg-black border-2 border-white/15 rounded-xl px-4 py-3 text-white text-sm leading-relaxed focus:outline-none focus:border-brand-orange resize-none"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mx-6 sm:mx-8 mb-4 px-4 py-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 text-sm">
            {error}
          </div>
        )}

        <div className="px-6 sm:px-8 py-5 border-t border-white/10 flex items-center justify-end gap-3 bg-black/40">
          <button
            type="button"
            data-testid={ADMIN.photoUploadCancel}
            onClick={onClose}
            className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs uppercase tracking-[0.2em] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid={ADMIN.photoUploadSubmit}
            disabled={submitting || !file}
            className="px-6 py-2.5 rounded-full bg-brand-orange text-black font-display font-black text-xs uppercase tracking-[0.2em] hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {submitting ? 'Uploading…' : 'Publish to Photo Dump →'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Photo row ────────────────────────────────────────────────────────────
const PhotoRow = ({ item, onDelete }) => {
  const isVideo = item.media_type === 'video';
  return (
    <div
      data-testid={`${ADMIN.photoRow}-${item.id}`}
      className="rounded-2xl bg-[#141414] border-2 border-white/10 p-4 grid grid-cols-12 gap-4 items-center"
    >
      <div className="col-span-3 sm:col-span-2">
        <div className="aspect-square rounded-xl overflow-hidden bg-black border border-white/10 relative">
          {isVideo ? (
            <>
              {item.poster
                ? <img src={`${API}${item.poster}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
                : <video src={`${API}${item.image}`} muted className="absolute inset-0 w-full h-full object-cover" />}
              <span className="absolute bottom-1.5 left-1.5 bg-white text-black font-display font-black text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full">▶</span>
            </>
          ) : (
            <img src={`${API}${item.image}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
        </div>
      </div>
      <div className="col-span-9 sm:col-span-7">
        <div className="font-display font-black text-white text-base truncate">{item.title}</div>
        <div className="text-xs text-white/60 line-clamp-2 mt-1">{item.description || '—'}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">
          {item.category} · {fmtDateTime(item.created_at)}
        </div>
      </div>
      <div className="col-span-12 sm:col-span-3 flex sm:justify-end">
        <button
          type="button"
          data-testid={`${ADMIN.photoDelete}-${item.id}`}
          onClick={() => {
            if (window.confirm(`Delete "${item.title}" from the Photo Dump?`)) onDelete(item.id);
          }}
          className="px-4 py-2 rounded-full bg-red-500/15 text-red-200 hover:bg-red-500/30 text-xs uppercase tracking-[0.2em] transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

// ── Reset confirmation dialog ─────────────────────────────────────────────
const ResetDialog = ({ token, onClose, onCleared }) => {
  const [scope, setScope] = useState('all');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/reset`,
        { confirm, scope },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onCleared(data.cleared);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not clear data.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
    >
      <form
        data-testid={ADMIN.resetDialog}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-[#141414] rounded-3xl border-2 border-red-500/40 shadow-[8px_8px_0_#dc2626] overflow-hidden"
      >
        <div className="px-6 sm:px-8 py-5 border-b border-white/10 bg-red-500/10">
          <div className="text-[10px] tracking-[0.32em] uppercase text-red-300">Danger zone →</div>
          <div className="font-display font-black text-2xl text-white mt-1">Clear test data</div>
        </div>

        <div className="px-6 sm:px-8 py-6 space-y-5 text-sm text-white/80">
          <p>
            This permanently deletes data from the database. Use this to wipe
            test entries before your real customers start coming in. Your
            uploaded photos and the curated Photo Dump are <strong className="text-white">not</strong> affected.
          </p>

          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">What should I clear?</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { v: 'all', label: 'Everything', test: ADMIN.resetScopeAll },
                { v: 'requests', label: 'Custom requests only', test: ADMIN.resetScopeRequests },
                { v: 'orders', label: 'Orders only', test: ADMIN.resetScopeOrders },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={opt.test}
                  onClick={() => setScope(opt.v)}
                  className={`px-4 py-2 rounded-xl text-xs uppercase tracking-[0.18em] font-display font-black border-2 transition-colors ${
                    scope === opt.v
                      ? 'border-red-400 bg-red-500/20 text-red-100'
                      : 'border-white/15 text-white/70 hover:border-white/40'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="reset-confirm" className="block text-[10px] tracking-[0.32em] uppercase text-white/60 mb-2">
              Type <span className="text-red-300 font-display font-black">CLEAR</span> to confirm
            </label>
            <input
              id="reset-confirm"
              data-testid={ADMIN.resetConfirmInput}
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="off"
              placeholder="CLEAR"
              className="w-full bg-black border-2 border-white/15 rounded-xl px-4 py-3 text-white font-display font-black tracking-[0.18em] uppercase focus:outline-none focus:border-red-400"
            />
          </div>

          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 sm:px-8 py-5 border-t border-white/10 bg-black/40 flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid={ADMIN.resetCancel}
            onClick={onClose}
            className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs uppercase tracking-[0.2em] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid={ADMIN.resetConfirm}
            disabled={submitting || confirm !== 'CLEAR'}
            className="px-6 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white font-display font-black text-xs uppercase tracking-[0.2em] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Clearing…' : 'Clear it →'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Dashboard ─────────────────────────────────────────────────────────────
const Dashboard = ({ token, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [community, setCommunity] = useState([]);
  const [communityCounts, setCommunityCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [tab, setTab] = useState('requests');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [info, setInfo] = useState(null);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [emailTesting, setEmailTesting] = useState(false);
  const [convertingRequest, setConvertingRequest] = useState(null);
  const [payLinkRequest, setPayLinkRequest] = useState(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [s, r, o, g, c] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { headers: authHeaders }),
        axios.get(`${API}/api/admin/requests`, { headers: authHeaders }),
        axios.get(`${API}/api/admin/orders`, { headers: authHeaders }),
        axios.get(`${API}/api/admin/gallery`, { headers: authHeaders }),
        axios.get(`${API}/api/admin/community`, { headers: authHeaders }),
      ]);
      setStats(s.data);
      setRequests(r.data);
      setOrders(o.data);
      setPhotos(g.data);
      setCommunity(c.data?.items || []);
      setCommunityCounts(c.data?.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        setError('Could not load admin data.');
      }
    }
  }, [authHeaders, onLogout]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/admin/requests/${id}`, { status }, { headers: authHeaders });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      loadAll();
    } catch (err) {
      setError('Could not update status.');
    }
  };

  const downloadCsv = async (kind) => {
    try {
      const res = await axios.get(`${API}/api/admin/${kind}.csv`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed.');
    }
  };

  const deletePhoto = async (id) => {
    try {
      await axios.delete(`${API}/api/admin/gallery/${id}`, { headers: authHeaders });
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch {
      setError('Could not delete that photo.');
    }
  };

  const approveSubmission = async (id) => {
    try {
      await axios.post(`${API}/api/admin/community/${id}/approve`, null, { headers: authHeaders });
      loadAll();
    } catch { setError('Could not approve.'); }
  };
  const rejectSubmission = async (id) => {
    try {
      await axios.post(`${API}/api/admin/community/${id}/reject`, null, { headers: authHeaders });
      loadAll();
    } catch { setError('Could not reject.'); }
  };
  const deleteSubmission = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this community submission? This is permanent.')) return;
    try {
      await axios.delete(`${API}/api/admin/community/${id}`, { headers: authHeaders });
      loadAll();
    } catch { setError('Could not delete.'); }
  };

  const deleteRequest = async (item) => {
    const label = item.name ? `${item.name}'s request` : 'this request';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/requests/${item.id}`, { headers: authHeaders });
      setRequests((prev) => prev.filter((r) => r.id !== item.id));
      setInfo(`Deleted request from ${item.name || 'customer'}.`);
      setTimeout(() => setInfo(null), 4000);
      loadAll();
    } catch {
      setError('Could not delete that request.');
    }
  };

  const deleteOrder = async (item) => {
    const label = item.product_name ? `"${item.product_name}"` : 'this order';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${label}? This removes the record from your dashboard only — Stripe still has the transaction.`)) return;
    try {
      await axios.delete(`${API}/api/admin/orders/${item.session_id}`, { headers: authHeaders });
      setOrders((prev) => prev.filter((o) => o.session_id !== item.session_id));
      setInfo(`Deleted order record.`);
      setTimeout(() => setInfo(null), 4000);
      loadAll();
    } catch {
      setError('Could not delete that order.');
    }
  };

  const unconvertRequest = async (item) => {
    try {
      await axios.post(
        `${API}/api/admin/requests/${item.id}/unconvert`,
        {},
        { headers: authHeaders },
      );
      setInfo(`Cleared the paid flag on ${item.name || 'this request'}. You can re-mark it as paid now.`);
      setTimeout(() => setInfo(null), 5000);
      loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not undo the converted status.');
    }
  };

  const runEmailTest = async () => {
    setEmailTesting(true);
    setEmailTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/email-test`, {}, { headers: authHeaders });
      setEmailTestResult(data);
    } catch (err) {
      setEmailTestResult({
        status: 'failed',
        error: err?.response?.data?.detail || err.message || 'Request failed',
        config: {},
      });
    } finally {
      setEmailTesting(false);
    }
  };

  const visibleRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    return requests
      .filter((r) => filter === 'all' ? true : r.status === filter)
      .filter((r) => !term ? true : (
        (r.name || '').toLowerCase().includes(term) ||
        (r.email || '').toLowerCase().includes(term) ||
        (r.idea || '').toLowerCase().includes(term)
      ));
  }, [requests, filter, search]);

  return (
    <div data-testid={ADMIN.dashboard} className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Top bar */}
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <a href="/" className="flex items-center gap-3">
            <img src="/brand/logo-mark.png" alt="" className="w-10 h-10 rounded-lg" />
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase text-brand-orange">Studio admin</div>
              <div className="font-display font-black text-xl leading-none">Shirt Happens</div>
            </div>
          </a>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid={ADMIN.emailTest}
            onClick={runEmailTest}
            disabled={emailTesting}
            className="text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            {emailTesting ? 'Testing…' : 'Test email'}
          </button>
          <button
            type="button"
            onClick={loadAll}
            className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white px-3 py-2"
          >
            Refresh
          </button>
          <button
            type="button"
            data-testid={ADMIN.resetOpen}
            onClick={() => setShowReset(true)}
            className="text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full bg-red-500/15 text-red-200 hover:bg-red-500/30 transition-colors"
          >
            Clear test data
          </button>
          <button
            type="button"
            data-testid={ADMIN.logout}
            onClick={onLogout}
            className="text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            Log out
          </button>
        </div>
      </header>

      <main className="px-6 sm:px-10 py-8 max-w-7xl mx-auto">
        {error && (
          <div className="mb-6 px-5 py-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 text-sm">
            {error}
          </div>
        )}
        {info && (
          <div className="mb-6 px-5 py-3 rounded-xl bg-green-500/15 border border-green-500/30 text-green-200 text-sm">
            {info}
          </div>
        )}
        {emailTestResult && (
          <div
            data-testid={ADMIN.emailTestResult}
            className={`mb-6 px-5 py-4 rounded-xl border text-sm leading-relaxed ${
              emailTestResult.status === 'sent'
                ? 'bg-green-500/15 border-green-500/30 text-green-100'
                : 'bg-red-500/15 border-red-500/30 text-red-100'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display font-black text-base mb-1">
                  {emailTestResult.status === 'sent'
                    ? '✓ Email config is working'
                    : '✕ Email config is broken'}
                </div>
                <div className="text-xs opacity-80 mb-1">
                  Sender: <code className="font-mono">{emailTestResult.config?.sender_email || '(unset)'}</code> ·{' '}
                  Owner: <code className="font-mono">{emailTestResult.config?.owner_email || '(unset)'}</code> ·{' '}
                  API key: {emailTestResult.config?.api_key_set ? 'set' : 'MISSING'}
                </div>
                {emailTestResult.config?.sender_fallback_applied && (
                  <div className="text-xs opacity-80 mb-1">
                    ⚠ Stale <code className="font-mono">SENDER_EMAIL={emailTestResult.config.sender_email_raw}</code> in env was auto-replaced with <code className="font-mono">{emailTestResult.config.sender_email}</code>. Update your production .env to silence this.
                  </div>
                )}
                {emailTestResult.status === 'sent' && (
                  <div className="text-xs opacity-80">A test email was just sent to your owner address.</div>
                )}
                {emailTestResult.error && (
                  <div className="text-xs mt-2 font-mono break-all opacity-80">{emailTestResult.error}</div>
                )}
                {emailTestResult.status !== 'sent' && (
                  <div className="text-xs mt-3 leading-relaxed">
                    Most common fix: log into <a href="https://app.sendgrid.com/settings/sender_auth/senders" target="_blank" rel="noopener noreferrer" className="underline">SendGrid → Sender Authentication</a>{' '}
                    and confirm <code className="font-mono">{emailTestResult.config?.sender_email || 'your sender'}</code> shows as <strong>Verified</strong> (green check). If it shows as Pending, open your Gmail and click the verification link SendGrid sent.
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setEmailTestResult(null)}
                className="opacity-60 hover:opacity-100 text-lg leading-none px-2"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4 mb-10">
            <Stat label="New requests" value={stats.new_requests} testid={ADMIN.statNewRequests} accent />
            <Stat label="Total requests" value={stats.total_requests} testid={ADMIN.statTotalRequests} />
            <Stat label="Paid orders" value={stats.paid_orders} testid={ADMIN.statPaidOrders} />
            <Stat label="Total orders" value={stats.total_orders} testid={ADMIN.statTotalOrders} />
            <Stat label="Revenue" value={`$${(stats.revenue || 0).toFixed(2)}`} testid={ADMIN.statRevenue} accent />
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div className="inline-flex rounded-full bg-[#141414] border-2 border-white/10 p-1">
            <button
              type="button"
              data-testid={ADMIN.tabRequests}
              onClick={() => setTab('requests')}
              className={`px-5 py-2 rounded-full text-xs tracking-[0.2em] uppercase font-display font-black transition-colors ${tab === 'requests' ? 'bg-brand-orange text-black' : 'text-white/70 hover:text-white'}`}
            >
              Requests ({requests.length})
            </button>
            <button
              type="button"
              data-testid={ADMIN.tabOrders}
              onClick={() => setTab('orders')}
              className={`px-5 py-2 rounded-full text-xs tracking-[0.2em] uppercase font-display font-black transition-colors ${tab === 'orders' ? 'bg-brand-orange text-black' : 'text-white/70 hover:text-white'}`}
            >
              Orders ({orders.length})
            </button>
            <button
              type="button"
              data-testid={ADMIN.tabPhotos}
              onClick={() => setTab('photos')}
              className={`px-5 py-2 rounded-full text-xs tracking-[0.2em] uppercase font-display font-black transition-colors ${tab === 'photos' ? 'bg-brand-orange text-black' : 'text-white/70 hover:text-white'}`}
            >
              Photos ({photos.length})
            </button>
            <button
              type="button"
              data-testid="admin-tab-community"
              onClick={() => setTab('community')}
              className={`px-5 py-2 rounded-full text-xs tracking-[0.2em] uppercase font-display font-black transition-colors ${tab === 'community' ? 'bg-brand-orange text-black' : 'text-white/70 hover:text-white'}`}
            >
              Community{communityCounts.pending > 0 ? ` · ${communityCounts.pending} pending` : ''}
            </button>
            <button
              type="button"
              data-testid={ADMIN.tabAnalytics}
              onClick={() => setTab('analytics')}
              className={`px-5 py-2 rounded-full text-xs tracking-[0.2em] uppercase font-display font-black transition-colors ${tab === 'analytics' ? 'bg-brand-orange text-black' : 'text-white/70 hover:text-white'}`}
            >
              Analytics
            </button>
          </div>
          <div className="flex items-center gap-3">
            {tab === 'requests' && (
              <>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name / email / idea…"
                  className="bg-[#141414] border-2 border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-orange w-64"
                />
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="bg-[#141414] border-2 border-white/10 rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/80 focus:outline-none"
                >
                  <option value="all">All statuses</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
                <button
                  type="button"
                  data-testid={ADMIN.exportRequests}
                  onClick={() => downloadCsv('requests')}
                  className="text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full bg-white text-black hover:bg-brand-orange transition-colors font-display font-black"
                >
                  Export CSV
                </button>
              </>
            )}
            {tab === 'orders' && (
              <button
                type="button"
                data-testid={ADMIN.exportOrders}
                onClick={() => downloadCsv('orders')}
                className="text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full bg-white text-black hover:bg-brand-orange transition-colors font-display font-black"
              >
                Export CSV
              </button>
            )}
            {tab === 'photos' && (
              <button
                type="button"
                data-testid={ADMIN.photoAddOpen}
                onClick={() => setShowUpload(true)}
                className="text-xs uppercase tracking-[0.2em] px-5 py-2 rounded-full bg-brand-orange text-black hover:opacity-90 transition-opacity font-display font-black"
              >
                + Add to Photo Dump
              </button>
            )}
          </div>
        </div>

        {/* Lists */}
        {tab === 'requests' && (
          <div className="space-y-3">
            {visibleRequests.length === 0 ? (
              <div className="text-center py-16 text-white/40 border-2 border-dashed border-white/10 rounded-2xl">
                Nothing here yet. New requests will land at the top.
              </div>
            ) : (
              visibleRequests.map((item) => (
                <RequestRow
                  key={item.id}
                  item={item}
                  onStatusChange={updateStatus}
                  onConvert={setConvertingRequest}
                  onSendPayLink={setPayLinkRequest}
                  onDelete={deleteRequest}
                  onUnconvert={unconvertRequest}
                />
              ))
            )}
          </div>
        )}

        {tab === 'orders' && (
          <div className="space-y-3">
            {orders.length === 0 ? (
              <div className="text-center py-16 text-white/40 border-2 border-dashed border-white/10 rounded-2xl">
                No orders yet.
              </div>
            ) : (
              orders.map((item) => <OrderRow key={item.session_id} item={item} onDelete={deleteOrder} />)
            )}
          </div>
        )}

        {tab === 'photos' && (
          <div className="space-y-3">
            <div className="mb-2 px-4 py-3 rounded-xl bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-xs leading-relaxed">
              Photos and videos you add here go straight to the top of the public Photo Dump grid. JPG, PNG, WebP, HEIC, MP4 and MOV are supported — files are auto-rotated and compressed for the web.
            </div>
            {photos.length === 0 ? (
              <div className="text-center py-16 text-white/40 border-2 border-dashed border-white/10 rounded-2xl">
                Nothing uploaded yet. Hit <span className="text-brand-orange">“+ Add to Photo Dump”</span> to publish your first piece.
              </div>
            ) : (
              photos.map((item) => (
                <PhotoRow key={item.id} item={item} onDelete={deletePhoto} />
              ))
            )}
          </div>
        )}

        {tab === 'community' && (
          <div className="space-y-3">
            <div className="mb-2 px-4 py-3 rounded-xl bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-xs leading-relaxed">
              Customer photo submissions. <strong>Pending</strong> ({communityCounts.pending}) show first — approve to publish to the public wall, reject to hide, delete to remove entirely. <strong>Approved</strong> ({communityCounts.approved}) are already live on shirthappensstudio.com.
            </div>
            {community.length === 0 ? (
              <div className="text-center py-16 text-white/40 border-2 border-dashed border-white/10 rounded-2xl">
                No submissions yet. The public "Community Wall" section drives customers here.
              </div>
            ) : (
              community
                .slice()
                .sort((a, b) => {
                  const order = { pending: 0, approved: 1, rejected: 2 };
                  return (order[a.status] ?? 9) - (order[b.status] ?? 9);
                })
                .map((sub) => (
                  <div
                    key={sub.id}
                    data-testid={`admin-community-row-${sub.id}`}
                    className="rounded-2xl bg-[#141414] border-2 border-white/10 p-4 flex flex-col sm:flex-row gap-4"
                  >
                    <a href={`${API}${sub.photo_url}`} target="_blank" rel="noopener noreferrer" className="shrink-0">
                      <img
                        src={`${API}${sub.photo_url}`}
                        alt={sub.name}
                        className="w-full sm:w-32 h-32 object-cover rounded-xl border border-white/10 hover:border-brand-orange transition-colors"
                      />
                    </a>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="font-display font-black text-white text-base">{sub.name}</div>
                        <span
                          className={[
                            'shrink-0 px-3 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase font-display font-black',
                            sub.status === 'approved' ? 'bg-green-400 text-black'
                              : sub.status === 'rejected' ? 'bg-red-400 text-black'
                              : 'bg-brand-orange text-black',
                          ].join(' ')}
                        >
                          {sub.status}
                        </span>
                      </div>
                      {sub.email && <div className="text-xs text-white/40 mb-2">{sub.email}</div>}
                      <p className="text-sm text-white/80 leading-relaxed mb-3 whitespace-pre-wrap">{sub.caption}</p>
                      <div className="text-[10px] text-white/40 mb-3">Submitted {fmtDateTime(sub.submitted_at)}</div>
                      <div className="flex flex-wrap gap-2">
                        {sub.status !== 'approved' && (
                          <button type="button" data-testid={`admin-community-approve-${sub.id}`} onClick={() => approveSubmission(sub.id)}
                            className="px-4 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase font-display font-black bg-green-400 text-black hover:bg-green-300 transition-colors">
                            ✓ Approve
                          </button>
                        )}
                        {sub.status !== 'rejected' && (
                          <button type="button" data-testid={`admin-community-reject-${sub.id}`} onClick={() => rejectSubmission(sub.id)}
                            className="px-4 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase font-display font-black bg-white/10 text-white hover:bg-white/20 transition-colors">
                            Reject
                          </button>
                        )}
                        <button type="button" data-testid={`admin-community-delete-${sub.id}`} onClick={() => deleteSubmission(sub.id)}
                          className="px-4 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase font-display font-black bg-transparent text-red-400/70 hover:text-red-300 border border-red-400/30 transition-colors">
                          🗑 Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        )}

        {tab === 'analytics' && <AnalyticsTab token={token} />}
      </main>

      {showUpload && (
        <PhotoUploadModal
          token={token}
          onClose={() => setShowUpload(false)}
          onUploaded={loadAll}
        />
      )}

      {showReset && (
        <ResetDialog
          token={token}
          onClose={() => setShowReset(false)}
          onCleared={(c) => {
            setInfo(`Cleared ${c.requests} request${c.requests === 1 ? '' : 's'} and ${c.orders} order${c.orders === 1 ? '' : 's'}.`);
            setTimeout(() => setInfo(null), 5000);
            loadAll();
          }}
        />
      )}

      {convertingRequest && (
        <ConvertToOrderDialog
          token={token}
          request={convertingRequest}
          onClose={() => setConvertingRequest(null)}
          onConverted={(order) => {
            setInfo(`Logged $${Number(order.amount).toFixed(2)} paid order — request marked done.`);
            setTimeout(() => setInfo(null), 5000);
            loadAll();
          }}
        />
      )}

      {payLinkRequest && (
        <SendPaymentLinkDialog
          token={token}
          request={payLinkRequest}
          onClose={() => setPayLinkRequest(null)}
          onSent={(data) => {
            const msg = data.email_status === 'sent'
              ? `Payment link sent to the customer.`
              : `Link created but email delivery ${data.email_status}. Copy manually: ${data.url}`;
            setInfo(msg);
            setTimeout(() => setInfo(null), 8000);
            loadAll();
          }}
        />
      )}
    </div>
  );
};

// ── Public wrapper ────────────────────────────────────────────────────────
const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  // Validate token on mount; if invalid, clear it
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
};

export default Admin;
